import { useMemo } from 'react';
import useStores from '@stores/useStores';
import { useActionGuard, ActionType, ResourceType } from '@hooks/useActionGuard';
import { constructPermissionResult } from '@hooks/useActionGuard/utils';
import { getMessageList } from '@lib/utils/getMessageList';
import { ApiAccount } from '@api';

import { AccountResourceType } from '../../store';

// The delete-denial reason for the data-retention account currently in use (see the
// hook below). Exported so both the hook and its tests reference one copy; the tests are
// outside the deadcode tsconfig, so ts-prune only sees the in-module use.
// ts-prune-ignore-next
export const IN_USE_ACCOUNT_DELETE_REASON =
  "You cannot delete the account that's currently in use. Switch to another account first.";

/**
 * Custom hook that computes delete permissions for a list of accounts.
 * This hook pre-computes permissions at the component level to avoid calling hooks
 * inside loops, which violates React's Rules of Hooks.
 *
 * Data-retention accounts are gated on org-admin. The account currently in use is NOT
 * deletable: deleting it would destroy the workspace's live storage config, so the
 * picker offers its delete control disabled with a reason until the user switches to
 * another account. Source/destination behavior (org-permission + linked-resource
 * guard) is preserved; their active account is hidden from delete by the picker's
 * `showDelete`, so `activeAccountId` only changes what data-retention returns.
 *
 * @param accounts - Array of accounts to check permissions for
 * @param resourceType - The kind of resource the accounts belong to
 * @param activeAccountId - The account currently in use (blocked from deletion for
 *   data retention); undefined when no account is active
 * @returns A map of account IDs to their delete permission results
 */
export const useDeleteAccountPermissions = (
  accounts: ApiAccount[],
  resourceType: AccountResourceType,
  activeAccountId?: string,
) => {
  const { userStore, permissionsStore, destinationsListStore, sourcesListStore } = useStores();
  const { canPerformAction, isNewPermissionModelEnforcementEnabled } = useActionGuard();

  const resourceLabel = resourceType === 'dataRetention' ? 'data retention' : resourceType;

  const dataRetentionAllowed = isNewPermissionModelEnforcementEnabled
    ? userStore.isOrgAdmin
    : permissionsStore.hasPermission({ resourceType: 'workspace', action: 'edit' });

  const sourceOrDestinationAllowed = canPerformAction({
    new: {
      actionType: ActionType.CREATE,
      resource: {
        type: resourceType === 'destination' ? ResourceType.DESTINATION : ResourceType.EVENT_STREAM,
      },
    },
    old: { resourceType: 'workspace', action: 'edit' },
  }).isAllowed;

  const isAllowed =
    resourceType === 'dataRetention' ? dataRetentionAllowed : sourceOrDestinationAllowed;

  const deleteAccountPermission = constructPermissionResult(
    isAllowed,
    `You do not have permission to delete ${resourceLabel} accounts.`,
  );

  const deletePermissionsMap = useMemo(
    () =>
      Object.fromEntries(
        accounts.map((account) => {
          let isInUse: boolean;
          let inUseReason: string;

          if (resourceType === 'destination') {
            isInUse = destinationsListStore.destinations.some(
              (resource) => resource.config.rudderAccountId === account.id,
            );
            inUseReason = 'You cannot delete an account with linked destinations.';
          } else if (resourceType === 'source') {
            isInUse = sourcesListStore.sources.some(
              (resource) => resource.config.rudderAccountId === account.id,
            );
            inUseReason = 'You cannot delete an account with linked sources.';
          } else {
            // Data-retention has a single workspace FK, not N linked resources: the
            // account "in use" is the active one. Deleting it would tear down the
            // workspace's live storage config, so it is blocked until the user
            // switches to another account.
            isInUse = account.id === activeAccountId;
            inUseReason = IN_USE_ACCOUNT_DELETE_REASON;
          }

          return [
            account.id,
            {
              isAllowed: deleteAccountPermission.isAllowed && !isInUse,
              denialReason: getMessageList([
                deleteAccountPermission.denialReason,
                isInUse ? inUseReason : '',
              ]),
            },
          ];
        }),
      ),
    [accounts, activeAccountId, resourceType],
  );

  return { deletePermissionsMap };
};
